import { AxiosInstance } from 'axios';
import { CountConsume, toCountConsume } from 'domain/event/CountConsume';
import { EventParticipant } from 'domain/event/participants/EventParticipant';
import { TypeBo } from 'domain/event/TypeBo';
import { TypeBracket } from 'domain/event/TypeBracket';
import { TypeEvent } from 'domain/event/TypeEvent';
import { TypeRonde } from 'domain/event/TypeRonde';
import { UnpyEventView } from 'domain/event/UnpyEventView';
import { CountHome, toCountHome } from 'domain/home/CountHome';
import { DelayEventStartDateFormData } from 'primary/classements/Ligue/Modal/ConfirmEvent/ModalConfirmEvent';
import { GenerateMatchesFormData } from 'primary/classements/Ligue/Modal/GenerateClassement/GenerateMatchesForm';
import { FormAddMissingPlayerData } from 'primary/Components/Event/RegisterEventFormStep/FormAddMissingPlayers';
import { EventRegisterFormData } from 'primary/Components/Event/RegisterEventFormStep/FormStepPlayers';
import { EventPublishFormData } from 'primary/events/forms/EventPublishForm';
import { EventRegisterParamsFormData } from 'primary/events/forms/EventRegisterParamsForm';
import { EventReopenSubscribeFormData } from 'primary/events/forms/EventReopenSubscribeForm';
import { EventUnpyAdminFormData } from 'primary/events/forms/EventUnpyAdminForm';
import { AxiosProviderRepository } from 'secondary/AxiosProviderRepository';
import { toEventParticipant } from 'secondary/event/participants/RestEventParticipant';
import { toUnpyEventView } from 'secondary/event/RestEventView';

import { IEventRepository } from '../../domain/event/Event.repository';
import { PlayerRoleEnum } from '../../domain/event/PlayerRoleEnum';
import { TypeTeam } from '../../domain/event/TypeTeam';
import { UnpyEvent, UnpyEventFilters } from '../../domain/event/UnpyEvent';
import { Pageable } from '../../domain/pageable/Pageable';
import { formatDate } from '../../helper/date.helper';
import { EventAppearenceFormData } from '../../primary/events/forms/EventAppearenceForm';
import { EventFormData } from '../../primary/events/forms/EventCreateForm';
import { EventParameterizeFormData } from '../../primary/events/forms/EventParameterizeForm';
import { FixtureEventFormData } from '../../primary/Parameters/user/AdminArea/EventsAdmin';
import { toPageable } from '../RestPageable';
import { RestEvent, toUnpyEvent } from './RestEvent';
import { RestTypeBo, toTypeBo } from './RestTypeBo';
import { RestTypeBracket, toTypeBracket } from './RestTypeBracket';
import { RestTypeEvent, toTypeEvent } from './RestTypeEvent';
import { RestTypeRonde, toTypeRonde } from './RestTypeRonde';
import { RestTypeTeam, toTypeTeam } from './RestTypeTeam';

export class EventRepository extends AxiosProviderRepository implements IEventRepository {
  constructor(readonly axios: AxiosInstance) {
    super(axios);
  }

  generateFixture(): Promise<void> {
    return this.axios.post('/unpy/api/fixtures/add');
  }

  create(eventForm: EventFormData): Promise<UnpyEvent> {
    return this.axios
      .post('/unpy/api/event/create', {
        ...eventForm,
        startDateTime: formatDate(
          new Date(eventForm.startDateTime),
          'back(datetime) UTC',
        ),
        endSubscribeDate: formatDate(
          new Date(eventForm.endSubscribeDate),
          'back(datetime) UTC',
        ),
        typeBracket: null,
        typeRonde: null,
        typeBo: null,
        teams: null,
        admins: null,
      })
      .then((response) => toUnpyEvent(response.data));
  }

  createFixtures(eventFixtureForm: FixtureEventFormData[]): Promise<UnpyEvent> {
    return this.axios
      .post('/unpy/api/fixtures/add', {
        events: eventFixtureForm,
      })
      .then((response) => toUnpyEvent(response.data));
  }

  updateEventAppearence(
    appearenceFormData: EventAppearenceFormData,
    idEvent: number,
    file: File,
  ): Promise<UnpyEvent> {
    const bodyFormData = new FormData();

    bodyFormData.set('image', file);
    bodyFormData.set(
      'appearenceForm',
      new Blob(
        [
          JSON.stringify({
            name: appearenceFormData.name,
            image: appearenceFormData.image,
            twitter: appearenceFormData.twitter,
            facebook: appearenceFormData.facebook,
            youtube: appearenceFormData.youtube,
            instagram: appearenceFormData.instagram,
            discord: appearenceFormData.discord,
            twitch: appearenceFormData.twitch,
            resizeProps: appearenceFormData.resizeProps,
          }),
        ],
        {
          type: 'application/json',
        },
      ),
    );

    return this.axios
      .put(`/unpy/api/event/${idEvent}/update/apparence`, bodyFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => toUnpyEvent(response.data));
  }

  getAllTypeBo(): Promise<TypeBo[]> {
    return this.axios
      .get('/unpy/api/event/codifs/type/bo/all')
      .then((response) =>
        response.data.map((restTypeBo: RestTypeBo) => toTypeBo(restTypeBo)),
      );
  }

  getAllTypeBracket(): Promise<TypeBracket[]> {
    return this.axios
      .get('/unpy/api/event/codifs/type/bracket/all')
      .then((response) =>
        response.data.map((restTypeBracket: RestTypeBracket) =>
          toTypeBracket(restTypeBracket),
        ),
      );
  }

  getAllTypeEvent(): Promise<TypeEvent[]> {
    return this.axios
      .get('/unpy/api/event/codifs/type/event/all')
      .then((response) =>
        response.data.map((restTypeEvent: RestTypeEvent) => toTypeEvent(restTypeEvent)),
      );
  }

  getAllTypeRonde(): Promise<TypeRonde[]> {
    return this.axios
      .get('/unpy/api/event/codifs/type/ronde/all')
      .then((response) =>
        response.data.map((restTypeRonde: RestTypeRonde) => toTypeRonde(restTypeRonde)),
      );
  }

  getAllTypeTeam(): Promise<TypeTeam[]> {
    return this.axios
      .get('/unpy/api/event/codifs/type/team/all')
      .then((response) =>
        response.data.map((restTypeTeam: RestTypeTeam) => toTypeTeam(restTypeTeam)),
      );
  }

  getEventById(idEvent: number | string): Promise<UnpyEvent> {
    return this.axios
      .get(`/unpy/api/event/${idEvent}`)
      .then((response) => toUnpyEvent(response.data));
  }

  getEventRegisterAvailableConnectedUserOwner(): Promise<RestEvent[]> {
    return this.axios
      .get(`/unpy/api/events/selector`)
      .then((response) => response.data.map((event: RestEvent) => toUnpyEvent(event)));
  }

  getEventsOverviewConnectedUser(): Promise<UnpyEvent[]> {
    return this.axios
      .get(`/unpy/api/events/overview/me`)
      .then((response) => response.data.map((event: RestEvent) => toUnpyEvent(event)));
  }

  searchOwn(
    filters: UnpyEventFilters,
    page: number,
    nbPerPage: number,
  ): Promise<Pageable<UnpyEventView>> {
    return this.axios
      .post('/unpy/api/events/search/own', {
        ...filters,
        page,
        nbPerPage,
      })
      .then((response) => toPageable(response, toUnpyEventView));
  }

  searchRegistered(
    filters: UnpyEventFilters,
    page: number,
    nbPerPage: number,
  ): Promise<Pageable<UnpyEventView>> {
    return this.axios
      .post('/unpy/api/events/search/registered', {
        ...filters,
        page,
        nbPerPage,
      })
      .then((response) => toPageable(response, toUnpyEventView));
  }

  searchFinished(
    filters: UnpyEventFilters,
    page: number,
    nbPerPage: number,
  ): Promise<Pageable<UnpyEventView>> {
    return this.axios
      .post('/unpy/api/events/search/terminated', {
        ...filters,
        page,
        nbPerPage,
      })
      .then((response) => toPageable(response, toUnpyEventView));
  }

  canCreateAnnoncement(eventId: number): Promise<boolean> {
    return this.axios
      .get(`/unpy/api/events/${eventId}/annoncement/check`)
      .then((response) => response.data.canCreate ?? false);
  }

  searchHome(game?: string[]): Promise<Pageable<UnpyEventView>> {
    return this.axios
      .post('/unpy/api/events/search/home', {
        game: game,
      })
      .then((response) => {
        return toPageable(response, toUnpyEventView);
      });
  }

  searchPublic(
    filters: UnpyEventFilters,
    page: number,
    nbPerPage: number,
  ): Promise<Pageable<UnpyEventView>> {
    return this.axios
      .post('/unpy/api/events/search/public', {
        ...filters,
        page,
        nbPerPage,
      })
      .then((response) => toPageable(response, toUnpyEventView));
  }

  getOverview(): Promise<UnpyEvent[]> {
    return this.axios.get(`/unpy/api/events/overview`).then((response) => {
      return response.data.map((event: RestEvent) => toUnpyEvent(event));
    });
  }

  updateEventRegisterParams(
    data: EventRegisterParamsFormData,
    idEvent: number,
  ): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/update/register/params`, {
        ...data,
        startDateTime: formatDate(new Date(data.startDateTime), 'back(datetime) UTC'),
        endSubscribeDate: formatDate(
          new Date(data.endSubscribeDate),
          'back(datetime) UTC',
        ),
      })
      .then((response) => toUnpyEvent(response.data));
  }

  updateEventParameterize(
    data: EventParameterizeFormData,
    idEvent: number,
  ): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/update/parameterize`, data)
      .then((response) => toUnpyEvent(response.data));
  }

  openEvent(data: EventPublishFormData, idEvent: number): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/step/open`, data)
      .then((response) => toUnpyEvent(response.data));
  }

  closeSubscriptionEvent(idEvent: number): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/step/subscription/close`)
      .then((response) => toUnpyEvent(response.data));
  }

  confirmStartDate(
    idEvent: number,
    data: DelayEventStartDateFormData,
  ): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/confirm`, data)
      .then((response) => toUnpyEvent(response.data));
  }

  startEvent(idEvent: number, data: GenerateMatchesFormData): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/step/start`, data)
      .then((response) => toUnpyEvent(response.data));
  }

  finishEvent(idEvent: number): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/step/finish`)
      .then((response) => toUnpyEvent(response.data));
  }

  cancelEvent(idEvent: number): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/step/cancel`)
      .then((response) => toUnpyEvent(response.data));
  }

  createTeam(data: EventRegisterFormData, idEvent: number): Promise<EventParticipant> {
    return this.axios
      .post(`/unpy/api/participant/event/${idEvent}/create`, {
        name: data.name,
        idEvent: idEvent,
        idStructure: data.structure,
        code: data.code,
        players: Object.keys(data.players).map((key, index) => {
          return {
            idUnpy: data.players[key].idPlayer,
            role: index === 0 ? PlayerRoleEnum.CHIEF : PlayerRoleEnum.TITULAR,
            withLink: data.players[key].withLink,
          };
        }),
      })
      .then((response) => toEventParticipant(response.data));
  }

  updateEventAsAsmin(data: EventUnpyAdminFormData, idEvent: number): Promise<UnpyEvent> {
    return this.axios.put(`/unpy/api/event/${idEvent}/admin/unpy/update`, data);
  }

  launchGarabageEventAsAdmin(): Promise<UnpyEvent> {
    return this.axios.get(`/unpy/api/event/admin/unpy/garabage`);
  }

  delayEvent(idEvent: number, data: DelayEventStartDateFormData): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/step/delay`, data)
      .then((response) => toUnpyEvent(response.data));
  }

  countEvents(): Promise<CountHome> {
    return this.axios
      .get(`/unpy/api/events/count`)
      .then((response) => toCountHome(response.data));
  }

  countEventsCreationConsume(): Promise<CountConsume> {
    return this.axios
      .get(`/unpy/api/events/count/created/consume`)
      .then((response) => toCountConsume(response.data));
  }

  reopenSubscriptions(
    data: EventReopenSubscribeFormData,
    idEvent: number,
  ): Promise<UnpyEvent> {
    return this.axios
      .put(`/unpy/api/event/${idEvent}/step/subscription/reopen`, data)
      .then((response) => toUnpyEvent(response.data));
  }

  completeTeam(data: FormAddMissingPlayerData): Promise<void> {
    return this.axios.put(`/unpy/api/team/complete`, {
      ...data,
      players: Object.keys(data.players).map((key) => {
        return {
          idPlayerInTeam: data.players[key].idPlayerInTeam,
          idUnpy: data.players[key].idPlayer,
          role: PlayerRoleEnum.TITULAR,
          withLink: data.players[key].withLink,
        };
      }),
    });
  }
}
